import React, { useState, useEffect } from 'react';
import './MeetingHorizonBar.css';

const pad = n => String(n).padStart(2, '0');

function msToUnits(ms) {
  const total = Math.max(0, ms);
  return {
    days:    Math.floor(total / 86400000),
    hours:   Math.floor((total % 86400000) / 3600000),
    minutes: Math.floor((total % 3600000) / 60000),
    seconds: Math.floor((total % 60000) / 1000),
  };
}

function CountdownUnits({ ms, urgency }) {
  const { days, hours, minutes, seconds } = msToUnits(ms);
  return (
    <div className="horizon__units">
      {days > 0 && (
        <>
          <div className={`horizon__unit horizon__unit--${urgency}`}>
            <span className="horizon__num">{pad(days)}</span>
            <span className="horizon__sub">d</span>
          </div>
          <span className="horizon__colon">:</span>
        </>
      )}
      <div className={`horizon__unit horizon__unit--${urgency}`}>
        <span className="horizon__num">{pad(hours)}</span>
        <span className="horizon__sub">h</span>
      </div>
      <span className="horizon__colon">:</span>
      <div className={`horizon__unit horizon__unit--${urgency}`}>
        <span className="horizon__num">{pad(minutes)}</span>
        <span className="horizon__sub">m</span>
      </div>
      <span className="horizon__colon">:</span>
      <div className={`horizon__unit horizon__unit--${urgency}`}>
        <span className="horizon__num">{pad(seconds)}</span>
        <span className="horizon__sub">s</span>
      </div>
    </div>
  );
}

function ProgressTrack({ fillPct, urgency }) {
  const showKnob = fillPct > 3 && fillPct < 98;
  return (
    <div className="horizon__track">
      <span className="horizon__milestone" style={{ left: '25%' }} />
      <span className="horizon__milestone" style={{ left: '50%' }} />
      <span className="horizon__milestone" style={{ left: '75%' }} />
      <div
        className={`horizon__bar horizon__bar--${urgency}`}
        style={{ width: `${fillPct}%` }}
      >
        <span className="horizon__shimmer" />
      </div>
      {showKnob && (
        <span
          className={`horizon__knob horizon__knob--${urgency}`}
          style={{ left: `${fillPct}%` }}
        />
      )}
    </div>
  );
}

export default function MeetingHorizonBar({ scheduledFor, scheduledEnd, createdAt }) {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick(n => n + 1), 1000);
    return () => clearInterval(id);
  }, []);

  const now      = Date.now();
  const startMs  = new Date(scheduledFor).getTime();
  const endMs    = scheduledEnd ? new Date(scheduledEnd).getTime() : null;
  const createdMs = new Date(createdAt).getTime();

  // ── STATE 1: Window not yet open ─────────────────────────────────────────
  if (now < startMs) {
    const remaining    = startMs - now;
    const elapsed      = now - createdMs;
    const totalToStart = Math.max(startMs - createdMs, 1);
    const fillPct      = Math.max(0, Math.min(100, (elapsed / totalToStart) * 100));
    const urgency      = fillPct > 92 ? 'urgent' : fillPct > 75 ? 'near' : fillPct > 45 ? 'mid' : 'far';

    const startDate  = new Date(startMs);
    const endDate    = endMs ? new Date(endMs) : null;
    const crossDay   = endDate && startDate.toDateString() !== endDate.toDateString();
    const endLabel   = endDate
      ? (crossDay
          ? endDate.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
          : endDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }))
      : null;

    return (
      <div className={`horizon horizon--pending horizon--${urgency}`}>
        <div className="horizon__header">
          <span className="horizon__dot horizon__dot--pending" />
          <span className="horizon__label">Window opens in</span>
        </div>
        <CountdownUnits ms={remaining} urgency={urgency} />
        <ProgressTrack fillPct={fillPct} urgency={urgency} />
        {endLabel && (
          <div className="horizon__window-span">
            <span>Opens</span>
            <span className="horizon__window-time">{startDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            <span className="horizon__window-arrow">→</span>
            <span>Closes</span>
            <span className="horizon__window-time">{endLabel}</span>
          </div>
        )}
      </div>
    );
  }

  // ── STATE 2: Window is LIVE ───────────────────────────────────────────────
  if (endMs && now >= startMs && now <= endMs) {
    const remaining = endMs - now;
    const total     = endMs - startMs;
    const fillPct   = Math.max(0, Math.min(100, (remaining / total) * 100));
    const urgency   = fillPct < 15 ? 'urgent' : fillPct < 35 ? 'near' : 'live';

    return (
      <div className={`horizon horizon--live horizon--${urgency}`}>
        <div className="horizon__header">
          <span className="horizon__dot horizon__dot--live" />
          <span className="horizon__label horizon__label--live">Window live · closes in</span>
        </div>
        <CountdownUnits ms={remaining} urgency={urgency} />
        <ProgressTrack fillPct={fillPct} urgency={urgency} />
      </div>
    );
  }

  // ── STATE 3: Window closed ────────────────────────────────────────────────
  if (endMs && now > endMs) {
    return (
      <div className="horizon horizon--closed">
        <div className="horizon__header">
          <span className="horizon__dot horizon__dot--closed" />
          <span className="horizon__label">Window expired</span>
        </div>
        <ProgressTrack fillPct={0} urgency="closed" />
      </div>
    );
  }

  // No end time set — fallback: ongoing open state
  return (
    <div className="horizon horizon--live">
      <div className="horizon__header">
        <span className="horizon__dot horizon__dot--live" />
        <span className="horizon__label horizon__label--live">Meeting window is open</span>
      </div>
    </div>
  );
}
